/**
 * Running the import a dialog left for this board (F-144): one ordinary
 * agent turn, applied without the review click only when the hand-off said
 * so (`PendingImport.autoApply` — a board created with the file). A plan
 * that would delete anything is left proposed regardless.
 */
import { SHORT_BOARD_COLUMN_NAMES, SOURCE_COLUMN_NAME, type BoardKind } from "@kola/shared";

import { scriptHeaderOp, withScriptHeader } from "../place-video/script-header.js";
import { applyAgentPlan } from "./apply.js";
import { applyTurn, sendAgentTurn, writerFor, type AgentSession } from "./controller.js";
import { planDeletes } from "./describe.js";
import { takePendingImport } from "./pending-import.js";
import type { AgentUiStore } from "./store.js";

/**
 * What the agent is asked when a board opens with a file waiting: copy it
 * in AS IT IS (F-150). On a long-form board, cutting it into beats,
 * translating and aligning are the step tasks the applied import leaves in
 * the panel, one button each. A Short's file (F-154) is the writer's whole
 * table — script, English, direction — and goes into the three columns the
 * Short was seeded with; nothing follows it.
 */
export function importOnOpenMessage(fileName: string, boardIsNew: boolean, kind: BoardKind = "long"): string {
  const where = boardIsNew ? "this new board" : "this board";
  if (kind === "shorts") {
    const [english, direction] = SHORT_BOARD_COLUMN_NAMES;
    return (
      `Fill ${where} from "${fileName}". It is a Short: map the file's script or dialogue column to ` +
      `"${SOURCE_COLUMN_NAME}", its English translation to "${english}", and its visual and sound direction to ` +
      `"${direction}" — create a column only for a file column none of these fits (or one the board lacks). ` +
      "Copy the rows exactly as they are — no split. Keep every row of the file." +
      (boardIsNew ? " Insert them at row 1, above the board's empty rows." : "")
    );
  }
  return (
    `Fill ${where} from "${fileName}". Map its script or dialogue column to "Original Script" and ` +
    "give every other column of the file a home (create columns as needed, in the file's order, under the " +
    "file's own names). Copy the rows exactly as they are — no split. Keep every row of the file."
  );
}

export type PendingImportOutcome = "none" | "applied" | "proposed" | "answered" | "failed";

/**
 * Run the import waiting for this board, if any. Opens the panel so the
 * transcript is visible, sends the turn, and applies or proposes the plan.
 */
export async function runPendingImport(session: AgentSession, ui: AgentUiStore): Promise<PendingImportOutcome> {
  const pending = takePendingImport(session.boardId);
  if (pending === undefined) return "none";
  ui.getState().setPanelOpen(true);
  // F-155: a Short from Local Files keeps its script's path on row 1 and a
  // Place video button on row 2 — named here, while the seed is all there is.
  const sheetId = session.store.getState().sheetId;
  const sourceColId = sheetId === null ? undefined : session.board.sourceColumnId(sheetId);
  const header =
    pending.kind === "shorts" && pending.sourcePath !== undefined && sheetId !== null && sourceColId !== undefined
      ? { sheetId, sourceColId, path: pending.sourcePath }
      : undefined;
  const turn = await sendAgentTurn(session, ui, {
    message: importOnOpenMessage(pending.attachment.name, pending.autoApply, pending.kind),
    attachments: [pending.attachment],
    model: ui.getState().model,
  });
  if (turn.plan === undefined || turn.plan.sheetId !== header?.sheetId) {
    // No plan to ride on (or one for another sheet): the header lands alone.
    if (header !== undefined) {
      await applyAgentPlan(writerFor(session), {
        sheetId: header.sheetId,
        summary: "Script path and Place video",
        ops: [scriptHeaderOp(header.sourceColId, header.path)],
      });
    }
  } else {
    // On the import's own plan, so one Ctrl+Z or Revert takes both back.
    ui.getState().updateTurn(turn.id, { plan: withScriptHeader(turn.plan, header.sourceColId, header.path) });
  }
  if (turn.error) return "failed";
  if (turn.plan === undefined) return "answered";
  // F-150: applying this turn — now or later, by hand — leaves the step tasks.
  // F-154: not on a Short, whose import is the whole job.
  if (pending.kind !== "shorts") ui.getState().updateTurn(turn.id, { importFile: pending.attachment.name });
  if (!pending.autoApply || planDeletes(turn.plan.ops)) return "proposed";
  return (await applyTurn(session, ui, turn.id)) ? "applied" : "proposed";
}
